import React, { useState, useEffect } from 'react';
import { DroppableCell } from './DroppableCell';
import { DraggablePostIt } from './DraggablePostIt';
import { MoodEntry, User } from '../../types';
import api from '../../services/api';
import { Filter, Users } from 'lucide-react';

interface HistoryMoodBoardProps {
    meetingId: number;
}

export const HistoryMoodBoard: React.FC<HistoryMoodBoardProps> = ({ meetingId }) => {
    const [entries, setEntries] = useState<MoodEntry[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [visibleUserIds, setVisibleUserIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/meetings/${meetingId}/mood-history`);
                const historyEntries: MoodEntry[] = res.data;
                setEntries(historyEntries);

                // Extract unique users
                const uniqueUsersMap = new Map<number, User>();
                historyEntries.forEach(e => {
                    if (e.user) {
                        uniqueUsersMap.set(e.user.id, e.user);
                    }
                });
                const uniqueUsers = Array.from(uniqueUsersMap.values());
                setUsers(uniqueUsers);

                // Initially show all
                setVisibleUserIds(new Set(uniqueUsers.map(u => u.id)));

            } catch (err) {
                console.error("Failed to fetch history", err);
            } finally {
                setLoading(false);
            }
        };
        if (meetingId) fetchHistory();
    }, [meetingId]);

    const toggleUser = (userId: number) => {
        const newSet = new Set(visibleUserIds);
        if (newSet.has(userId)) {
            newSet.delete(userId);
        } else {
            newSet.add(userId);
        }
        setVisibleUserIds(newSet);
    };

    const toggleAll = () => {
        if (visibleUserIds.size === users.length) {
            setVisibleUserIds(new Set());
        } else {
            setVisibleUserIds(new Set(users.map(u => u.id)));
        }
    };

    // Render Grid
    const renderGrid = () => {
        const grid = [];
        for (let y = 7; y >= 0; y--) {
            for (let x = 0; x < 8; x++) {
                // Find all entries at this coordinate that match filter
                const cellEntries = entries.filter(e =>
                    e.x_coord === x &&
                    e.y_coord === y &&
                    (e.user_id ? visibleUserIds.has(e.user_id) : true)
                );

                grid.push(
                    <div key={`${x}-${y}`} className="w-full h-full aspect-square relative group">
                        <DroppableCell x={x} y={y}>
                            {/* Stack entries slightly if multiple */}
                            {cellEntries.map((entry, idx) => (
                                <div
                                    key={entry.id}
                                    className="absolute inset-0 transition-transform hover:scale-110 hover:z-50"
                                    style={{
                                        transform: `translate(${idx * 2}px, ${idx * 2}px)`,
                                        zIndex: idx
                                    }}
                                    title={`${entry.user?.full_name} (${new Date(entry.created_at || '').toLocaleDateString()})`}
                                >
                                    <DraggablePostIt
                                        id={`postit-${entry.id}`}
                                        content={entry.user?.full_name || 'User'}
                                        color={entry.user?.color_preference}
                                    />
                                </div>
                            ))}
                        </DroppableCell>
                    </div>
                );
            }
        }
        return grid;
    };

    if (loading) {
        return <div className="text-center p-8">Loading history...</div>;
    }

    return (
        <div className="flex flex-col lg:flex-row gap-8 w-full max-w-7xl mx-auto p-4">

            {/* Board */}
            <div className="flex-1 flex flex-col items-center">
                <div className="relative w-full aspect-square max-w-[800px]">
                    {/* Y-Axis Label */}
                    <div className="absolute -left-12 top-0 bottom-0 flex items-center justify-center z-10">
                        <span className="text-lg font-bold -rotate-90 tracking-widest text-muted whitespace-nowrap">ENERGY</span>
                    </div>

                    {/* X-Axis Label */}
                    <div className="absolute left-0 right-0 -bottom-8 flex items-center justify-center">
                        <span className="text-lg font-bold tracking-widest text-muted">PLEASANTNESS</span>
                    </div>

                    <div className="grid grid-cols-8 grid-rows-8 w-full h-full border-2 border-border-base bg-surface shadow-2xl rounded-lg overflow-hidden">
                        {renderGrid()}
                    </div>
                </div>
            </div>

            {/* Legend / Filter */}
            <div className="w-full lg:w-80 space-y-4">
                <div className="bg-surface p-6 rounded shadow-card border border-border-base">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 font-bold text-main">
                            <Filter className="w-5 h-5" />
                            <span>Filter Members</span>
                        </div>
                        <button
                            onClick={toggleAll}
                            className="text-sm text-primary hover:text-primary-hover font-medium decoration-1 underline-offset-2 hover:underline"
                        >
                            {visibleUserIds.size === users.length ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                        {users.map(user => (
                            <button
                                key={user.id}
                                onClick={() => toggleUser(user.id)}
                                className={`w-full flex items-center gap-3 p-2 rounded transition-all ${visibleUserIds.has(user.id)
                                    ? 'bg-surface-subtle border-border-base shadow-sm'
                                    : 'opacity-50 hover:opacity-75'
                                    } border border-transparent`}
                            >
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                                    style={{ backgroundColor: user.color_preference || '#000' }}
                                >
                                    {user.full_name?.charAt(0)}
                                </div>
                                <span className={`font-medium truncate ${visibleUserIds.has(user.id) ? 'text-main' : 'text-muted'}`}>
                                    {user.full_name}
                                </span>
                            </button>
                        ))}
                        {users.length === 0 && (
                            <p className="text-muted text-center py-4">No history data yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
